import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContractHistoryEntry } from '../types';
import { newestFirst } from '../utils/newestFirst';

interface ContractHistoryProps {
  entries: ContractHistoryEntry[];
  /** Shown in the export header, e.g. "Kontrakt #3". */
  contractLabel?: string;
  className?: string;
}

const DEFAULT_VISIBLE = 5;

const formatTimestamp = (iso: string) =>
  new Date(iso).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

/**
 * Immutable contract history (comments + status events) shown in the contract
 * view between the status message and the action buttons, newest first. Read-only
 * and identical for every persona. Exports the full list to PDF via print, in
 * chronological order.
 */
export function ContractHistory({ entries, contractLabel, className }: ContractHistoryProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const roleLabel = (role: string | null) =>
    role ? t(`contracts.history.role.${role}`, { defaultValue: role }) : '';
  const eventLabel = (eventType: string | null) =>
    eventType
      ? t(`contracts.history.event.${eventType}`, { defaultValue: eventType })
      : '';

  // Shown newest first (the latest reply without scrolling); collapsed, the
  // five most recent entries are visible. The PDF export stays chronological
  // so it reads as a record of what happened.
  const ordered = useMemo(() => newestFirst(entries), [entries]);
  const chronological = useMemo(() => [...ordered].reverse(), [ordered]);
  const hasMore = ordered.length > DEFAULT_VISIBLE;
  const visible = expanded || !hasMore ? ordered : ordered.slice(0, DEFAULT_VISIBLE);

  const exportPdf = () => {
    const rows = chronological
      .map((e) => {
        const who = [e.authorName, roleLabel(e.role)].filter(Boolean).join(' · ');
        const label = e.kind === 'event' ? eventLabel(e.eventType) : t('contracts.history.commentTag', 'Kommentar');
        return `<tr>
          <td>${formatTimestamp(e.createdAt)}</td>
          <td>${escapeHtml(label)}</td>
          <td>${escapeHtml(who)}</td>
          <td>${escapeHtml(e.message)}</td>
        </tr>`;
      })
      .join('');
    const title = `${t('contracts.history.title', 'Historik')}${contractLabel ? ` — ${contractLabel}` : ''}`;
    // NOTE: do NOT pass 'noopener' — window.open then returns null and nothing
    // opens. Print after the new document has loaded so it isn't blank.
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#212529;padding:24px;}
        h1{font-size:18px;margin:0 0 4px;}
        .sub{color:#6c757d;font-size:12px;margin:0 0 16px;}
        table{width:100%;border-collapse:collapse;font-size:12px;}
        th,td{border:1px solid #dee2e6;padding:6px 8px;text-align:left;vertical-align:top;}
        th{background:#f1f3f5;}
      </style></head><body onload="window.focus();window.print();">
      <h1>${escapeHtml(title)}</h1>
      <p class="sub">${escapeHtml(t('contracts.history.exportedAt', 'Exporterad'))}: ${formatTimestamp(new Date().toISOString())}</p>
      <table><thead><tr>
        <th>${escapeHtml(t('contracts.history.col.time', 'Tidpunkt'))}</th>
        <th>${escapeHtml(t('contracts.history.col.type', 'Typ'))}</th>
        <th>${escapeHtml(t('contracts.history.col.actor', 'Aktör'))}</th>
        <th>${escapeHtml(t('contracts.history.col.text', 'Text'))}</th>
      </tr></thead><tbody>${rows}</tbody></table>
      </body></html>`;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      // Popup blocked — fall back to a same-tab print via a hidden iframe.
      printViaIframe(html);
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <section className={`contract-history border rounded p-3 mb-3 ${className ?? ''}`.trim()} aria-label={t('contracts.history.title', 'Historik')}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h6 className="fw-bold mb-0">
          <i className="feather-clock me-2"></i>
          {t('contracts.history.title', 'Historik')}
        </h6>
        {ordered.length > 0 && (
          <span className="badge bg-secondary" style={{ fontSize: 10 }}>{ordered.length}</span>
        )}
        {ordered.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-light-brand ms-auto contract-history-export"
            onClick={exportPdf}
          >
            <i className="feather-download me-1"></i>
            {t('contracts.history.exportPdf', 'Exportera PDF')}
          </button>
        )}
      </div>

      {ordered.length === 0 ? (
        <p className="text-muted small mb-0">{t('contracts.history.empty', 'Ingen dialog eller händelse än.')}</p>
      ) : (
        <>
          {hasMore && !expanded && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 mb-2"
              onClick={() => setExpanded(true)}
            >
              {t('contracts.history.showAll', { defaultValue: 'Visa alla {{count}}', count: ordered.length })}
            </button>
          )}
          <ol className="list-unstyled mb-0 d-flex flex-column gap-2">
            {visible.map((e) => {
              const isEvent = e.kind === 'event';
              const who = [e.authorName, roleLabel(e.role)].filter(Boolean).join(' · ');
              const title = [
                isEvent ? eventLabel(e.eventType) : t('contracts.history.commentTag', 'Kommentar'),
                who
              ].filter(Boolean).join(' · ');
              return (
                <li key={e.id} className="contract-history-entry">
                  <time dateTime={e.createdAt} className="contract-history-entry__time">
                    {formatTimestamp(e.createdAt)}
                  </time>
                  <div className="contract-history-entry__title">{title}</div>
                  {e.message && (
                    <div className="contract-history-entry__body">{e.message}</div>
                  )}
                </li>
              );
            })}
          </ol>
          {hasMore && expanded && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 mt-2"
              onClick={() => setExpanded(false)}
            >
              {t('contracts.history.showLess', 'Visa färre')}
            </button>
          )}
        </>
      )}
    </section>
  );
}

/** Same-tab print fallback for when the browser blocks the popup window. */
function printViaIframe(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html.replace('window.focus();window.print();', ''));
  doc.close();
  const cleanup = () => setTimeout(() => iframe.remove(), 1000);
  iframe.contentWindow?.addEventListener('afterprint', cleanup);
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    cleanup();
  }, 250);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
